import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useStreamingChat } from "../../../hooks/useStreamingChat";
import { useQuickActions } from "../../../hooks/useQuickActions";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getQuickActions } from "../../../server/ai-brain";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/ai/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.aiBrain.quickActions(session.user.id),
      queryFn: async () => {
        const result = await getQuickActions({
          data: { userId: session.user.id, accessToken: session.access_token },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.moderate,
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="list" />,
  errorComponent: createRouteError("AI Brain"),
  component: AiBrainPage,
});

function AiBrainPage() {
  const { user, session, loading, isAuthenticated } = useAuth();
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { messages, sendMessage, isLoading, error, clear } = useStreamingChat();

  const { data: quickActions, isLoading: quickActionsLoading } = useQuickActions({
    userId: user?.id ?? "",
    accessToken: session?.access_token ?? "",
  });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput("");
    await sendMessage(text);
  }, [input, isLoading, sendMessage]);

  const handleQuickAction = useCallback((prompt: string) => {
    setInput(prompt);
  }, []);

  if (loading || !isAuthenticated || !user || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Streaming indicator banner */}
      <div className="bg-primary/5 border-b px-4 py-2 flex items-center gap-2">
        <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
        <span className="text-xs text-muted-foreground">
          Powered by TanStack AI + Gemini · streaming enabled
        </span>
        {messages.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clear}
            className="ml-auto text-xs"
          >
            Clear chat
          </Button>
        )}
      </div>

      <main className="container mx-auto flex-1 flex flex-col md:flex-row gap-4 p-4 max-w-5xl">
        <section className="flex-1 flex flex-col min-h-0 rounded-lg border bg-card" style={{ height: "calc(100vh - 12rem)" }}>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
            {messages.length === 0 && !isLoading && (
              <p className="text-sm text-muted-foreground">
                Send a message or pick a quick action. Responses stream in real-time.
              </p>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`rounded-lg px-3 py-2 text-sm ${
                  msg.role === "user"
                    ? "bg-primary/10 text-primary-foreground ml-6"
                    : "bg-muted text-muted-foreground mr-6"
                }`}
              >
                <span className="font-medium capitalize text-foreground">
                  {msg.role}:
                </span>{" "}
                <span className="whitespace-pre-wrap">{msg.content}</span>
              </div>
            ))}
            {isLoading && (
              <div className="rounded-lg px-3 py-2 text-sm bg-muted text-muted-foreground mr-6 flex items-center gap-2">
                <span className="font-medium text-foreground">assistant:</span>
                <span className="animate-pulse">●●●</span>
              </div>
            )}
            {error && (
              <div className="rounded-lg px-3 py-2 text-sm bg-destructive/10 text-destructive mr-6">
                <span className="font-medium">Error:</span> {error.message}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-4 border-t bg-card">
            <div className="flex gap-2">
              <Input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && !e.shiftKey && handleSend()
                }
                placeholder="Type a message…"
                className="flex-1"
                disabled={isLoading}
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim()}
                loading={isLoading}
              >
                Send
              </Button>
            </div>
          </div>
        </section>

        <aside className="w-full md:w-64 shrink-0 rounded-lg border bg-card p-4">
          <h2 className="text-sm font-semibold text-card-foreground mb-2">
            Quick actions
          </h2>
          {quickActionsLoading && (
            <p className="text-xs text-muted-foreground">Loading…</p>
          )}
          {!quickActionsLoading && quickActions && quickActions.length > 0 && (
            <ul className="space-y-1">
              {quickActions.map((action) => (
                <li key={action.id}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleQuickAction(action.prompt)}
                    className="w-full justify-start text-xs"
                    title={action.description}
                  >
                    {action.label}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {!quickActionsLoading &&
            (!quickActions || quickActions.length === 0) && (
              <p className="text-xs text-muted-foreground">
                No quick actions available.
              </p>
            )}
        </aside>
      </main>
    </div>
  );
}

